import React from 'react'
import { Tooltip, Text, Icon } from '@chakra-ui/react'

import { useMessageProperties } from '../message-provider'
import { isFailedPushOnlySend } from '../message-helpers'
import { getClientFullName, getPhoneCountryFromCode } from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import {
    ChatMessageFieldsFragment,
    MessageMethod,
    MessageOrigin,
    MessageType,
} from '~graphql/generated/graphql'
import {
    FaAddressCard,
    FaCommentDots,
    FaEnvelope,
    FaMessage,
    FaMobileScreenButton,
    FaNoteSticky,
    FaRobot,
} from 'react-icons/fa6'
import IconWithTooltip from '~components/shared/icons/icon-with-tooltip'

export default function MessageMethods() {
    const { message } = useMessageProperties()
    const isInternal = !!message.hidden_for_client
    const isPreview = message.is_preview
    const isSystem =
        message.message_type === MessageType.System ||
        message.message_origin === MessageOrigin.System
    const origin = message.message_origin
    const methods = message.message_methods || []

    return (
        <IconWithTooltip
            icon={getMethodIcon(methods.at(0), origin, isInternal, isSystem)}
            label={getMethodLabel(message, origin, isInternal, isPreview)}
            color="blackAlpha.500"
            fontSize="12px"
            tooltipOverrides={{
                bg: 'white',
                borderColor: 'blackAlpha.200',
                borderWidth: 1,
                fontSize: '12px',
                color: 'gray.700',
            }}
        />
    )
}

function getMethodIcon(
    method: MessageMethod | undefined,
    origin: MessageOrigin | null | undefined,
    isInternal: boolean,
    isSystem: boolean,
) {
    if (isInternal) {
        if (isSystem) {
            return FaRobot
        }
        return FaNoteSticky
    }

    if (!method) {
        switch (origin) {
            case MessageOrigin.Sms:
                return FaCommentDots
            case MessageOrigin.Email:
                return FaEnvelope
            default:
                return FaMessage
        }
    }

    switch (method) {
        case MessageMethod.Email:
            return FaEnvelope
        case MessageMethod.Sms:
            return FaCommentDots
        case MessageMethod.Push:
            return FaMobileScreenButton
        case MessageMethod.Postcard:
            return FaAddressCard
        default:
            return FaMessage
    }
}

function getMethodLabel(
    message: ChatMessageFieldsFragment,
    origin: MessageOrigin | null | undefined,
    isInternal: boolean,
    isPreview: boolean,
): string {
    if (isPreview) {
        return 'Automated Preview Message'
    }
    if (isInternal) {
        return 'Internal Note'
    }

    if (isFailedPushOnlySend(message)) {
        return `Not sent to ${getClientFullName(message.client, true) ?? ''} via Push`
    }

    const methods = message.message_methods || []
    const isIncoming = message.client
    const isFromSms = message.message_origin === MessageOrigin.Sms

    const labels: string[] = [`Sent ${isIncoming ? 'from' : 'to'}`]

    if (!methods.length) {
        switch (origin) {
            case MessageOrigin.Sms:
                return `Sent from ${getClientFullName(message.client, true) ?? ''} via SMS (${formatPhoneReadable(message.client_phone?.phone ?? message.client?.phone, message?.client_phone?.country_code ? getPhoneCountryFromCode(message.client_phone?.country_code || '') : message.chat?.clinic?.address?.country)})`
            case MessageOrigin.Email:
                return `Sent from ${getClientFullName(message.client, true) ?? ''} via Email`
            default:
                return `Sent from ${getClientFullName(message.client, true) ?? ''} via Chckvet Chat`
        }
    }

    methods.forEach((method, index) => {
        switch (method) {
            case MessageMethod.Email:
                labels.push(
                    `${index > 0 ? ' & ' : ''} ${message?.client?.email || ''} via Email`,
                )
                break
            case MessageMethod.Sms:
                const clientPhoneNameRaw = message.client_phone?.name || ''
                const clientPhoneName =
                    clientPhoneNameRaw.length > 10
                        ? clientPhoneNameRaw.slice(0, 10) + '...'
                        : clientPhoneNameRaw

                if (message.client_phone) {
                    labels.push(
                        `${index > 0 ? ' & ' : ''} ${formatPhoneReadable(message.client_phone.phone, message?.client_phone?.country_code ? getPhoneCountryFromCode(message.client_phone?.country_code || '') : message.chat?.clinic?.address?.country)} ${isFromSms ? `(${clientPhoneName.slice(0, 10)})` : ''} via SMS`,
                    )
                }

                break
            case MessageMethod.Push:
                labels.push(
                    `${index > 0 ? ' & ' : ''} ${getClientFullName(message.client, true) ?? ''} via Push`,
                )
                break
            case MessageMethod.Postcard:
                labels.push(
                    `${index > 0 ? ' & ' : ''} via postcard to ${getClientFullName(message.client, true) ?? ''}`,
                )
                break
            default:
                labels.push(
                    `${index > 0 ? ' & ' : ''} ${getClientFullName(message.client, true) ?? ''} via Chckvet Chat`,
                )
        }
    })

    return labels.join('')
}
